import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import { goFetch } from "@/utils/network-utils";
import { clientStateManager } from "@/services/error-services";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import type {
    Category,
    ContentfulItem,
    ContentfulWebinar,
    Countable,
    NormalizedContent,
    Product,
    Webinar
} from "@tmi/types/entities";

import type { Webinars } from "@tmi/types/api/content-models";

import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { formatDateAsISO8601String, normalizeDateTime, utcNow } from "@tmi/utils/date-time";

import LocalForagePlugin, { syncState, webinars } from "@/plugins/LocalForagePlugin";

import { normalizeWebinar } from "@/services/data/sync/webinars-sync-coordinator";
import { hydrateContent } from "@/services/data/sync/content-hydration";
import { DateTime } from "luxon";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

// State values
const isLoading = computed< boolean >( () => loading.value || fetching.value );

const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );

const initialized = computed< boolean >( () => allWebinars.value.length > 0 );
const allWebinars = ref< Webinar[] >( [] as unknown as Webinar[] );

export const useWebinarsStore = defineStore( 'webinars', () => {
    const permissionsStore = usePermissionsStore();

    const appStore = useApplicationStore();
    const { authToken, isAuthenticated } = storeToRefs( useAuthStore() );

    /**
     * Clear all state values for this store
     */
    function reset(): void {
        allWebinars.value.splice( 0 );
        fetching.value = false;
        loading.value = false;
    }

    function initialize(): void {
        if ( !authToken.value || initialized.value || loading.value ) {
            return;
        }

        loading.value = true;
        const t0: number = performance.now();

        if ( typeof webinars === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        webinars.ready()
            .then( () => {
                webinars.length()
                    .then( ( val: number ) => {
                        if ( val > 0 ) {
                            const loadedWebinars: Webinar[] = [];
                            // Iterate over all elements within IDB
                            webinars.iterate( ( item: NormalizedContent ) => {
                                loadedWebinars.push( hydrateContent( item, appStore.compositionCtx ) );
                                return;
                            } ).then( () => {
                                mergeFilterAndSortWebinarCatalog( loadedWebinars );

                                const t1: number = performance.now();
                                logger.info( `(local) Loaded ${ loadedWebinars.length } webinars in ${ t1 - t0 } ms` );
                            } ).catch( ( err: any ) => {
                                // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                                reset();

                                clientStateManager.queueClientError( err, {} );
                            } ).finally( () => loading.value = false );
                        } else {
                            refreshWebinarsFromServer( formatDateAsISO8601String( DateTime.utc().plus( { days: -90 } ) ) )
                                .then( ( webinars: Webinar[] ) => {
                                    mergeFilterAndSortWebinarCatalog( webinars );

                                    const t1: number = performance.now();
                                    logger.log( `Fetching & mapping ${ webinars.length } webinars took ${ t1 - t0 } ms` );
                                } ).catch( ( err: any ) => {
                                // If anything went wrong while attempting to initialize the store, reset it and then handle the specific error
                                reset();

                                clientStateManager.queueClientError( err, {} );
                            } ).finally( () => loading.value = false );
                        }
                    } );
            });
    }

    async function fetchWebinarBySlugIfMissing( slug: string ): Promise< Webinar | undefined > {
        // Best case scenario is that the article has already been loaded and is immediately available
        let requestedWebinar: Webinar | undefined = allWebinars.value.find( ( w: Webinar ) => w.slug === slug );
        if ( requestedWebinar ) {
            return requestedWebinar;
        }

        try {
            fetching.value = true;
            const response =
                await goFetch< Webinars, PersistedQueryPayload >( {
                    serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '3c2544b67e317fe059b79540419ca3c2',
                        variables: {
                            skip: 0,
                            limit: 1,
                            contentFilter: {
                                slugs: [ slug ]
                            }
                        }
                    },
                    authToken: authToken.value
                } );

            if ( null != response?.data?.webinarCatalog?.webinars ) {
                response.data.webinarCatalog.webinars
                    .map( ( w: ContentfulWebinar ) => {
                        const normalized = normalizeWebinar( w );
                        if ( normalized ) {
                            requestedWebinar = hydrateContent( normalized, appStore.compositionCtx );
                        }
                    } );
            }
        } catch ( e: any ) {
            logger.error( JSON.stringify( e.message ) );
            return undefined;
        } finally {
            fetching.value = false;
        }

        return requestedWebinar;
    }

    async function refreshWebinarsFromServer( latestUtcDate: string ): Promise< Webinar[] > {
        let retrievedWebinars: Webinar[] = [];

        const response =
            await goFetch< Webinars, PersistedQueryPayload >( {
                serverBaseUri: `${ import.meta.env.VITE_CONTENT_BASE_URI }`,
                endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: '3c2544b67e317fe059b79540419ca3c2',
                    variables: {
                        skip: 0,
                        limit: 250,
                        contentFilter: {
                            fromPublishedDateTimeUTC: latestUtcDate
                        }
                    }
                },
                authToken: authToken.value
            } );

        if ( null != response?.data?.webinarCatalog?.webinars ) {
            response?.data.webinarCatalog.webinars
                .map( ( webinarContent: ContentfulWebinar ) => {
                    const normalized = normalizeWebinar( webinarContent );
                    if ( normalized ) {
                        retrievedWebinars.push( hydrateContent( normalized, appStore.compositionCtx ) );
                    }
                } );
        }

        return retrievedWebinars;
    }

    function webinarBySlug( slug: string ): Webinar | undefined {
        return allWebinars.value.find( ( webinar: Webinar ): boolean => webinar.slug === slug );
    }

    function webinarById( id: string ): Webinar | undefined {
        return allWebinars.value.find( ( webinar: Webinar ): boolean => webinar.contentfulId === id );
    }

    function nUpcomingWebinars( webinarCount: number ): Webinar[] {
        const nowMinusOneHour = utcNow().minus( { hours: 1 } );
        return allWebinars.value
            .filter( ( webinar: Webinar ) =>
                normalizeDateTime( webinar.webinarDate ) ? normalizeDateTime( webinar.webinarDate )! > nowMinusOneHour : false )
                .slice( -1 * webinarCount - 1, -1 )
            .reverse();
    }

    /**
     * Performs a merge and sort (based on publication date and time of the ITradeAlert) with the current collection of trade
     * alerts and any new trade alerts that are loaded.
     *
     * @param {Webinar[]} newWebinars - the newly loaded collection of Webinar objects that need to be added
     * @returns {Webinar[]} - the aggregated collection of Webinar objects sorted by their publication date & time
     */
    function mergeFilterAndSortWebinarCatalog( newWebinars: Webinar[] ): void {
        logger.debug( 'mergeAndSortWebinarCatalog - existing: %i, new: %i', [ allWebinars.value.length, newWebinars.length ] );

        const uniqueWebinars = new Map( allWebinars.value.map( ( webinar: Webinar ) => [ webinar.contentfulId, webinar ] ) );
        newWebinars.forEach( ( newWebinar: Webinar ): void => {
            // Always overwrite whatever was previously in the collection (fucking crazy editing process, why fix it
            // when you can just make everything else more fucking complicated)
            uniqueWebinars.set( newWebinar.contentfulId, newWebinar );
        } );

        allWebinars.value = [ ...( Array.from( uniqueWebinars.values() ) )
            // Filter out any content to which the current user has not been granted access
            .filter( ( webinar: Webinar ) => {
                if ( !isFreelyAvailableContent( webinar.products.map( p => p.slug ) ) ) {
                    return isAuthenticated.value && webinar.products.some( ( p: Product ) =>
                        !!( p.codes ) && permissionsStore.hasAccessBasedOnShortCode( p.codes! )
                    );
                }

                return true;
            } )
            .sort( ( w1: Webinar, w2: Webinar ) => w2.publishedDate?.localeCompare( w1.publishedDate ) ?? -1 )
        ];

        logger.debug( '[ Webinars ] mergeAndSortWebinarCatalog - combined: ', allWebinars.value.length );
    }

    // This is implemented as a computed property to avoid unnecessarily calculating it multiple times
    const webinarCategories = computed< Category[] >( () => {
        // Contains the full catalog of all webinars the current user has access to with all dependent objects associated
        const categoryCountMap: Map< string, Countable< Category > > = new Map< string, Countable< Category > >();
        allWebinars.value.forEach( ( item: ContentfulItem ): void => {
            item.categories.forEach( ( category: Category ): void => {
                if ( !categoryCountMap.has( category.slug ) ) {
                    categoryCountMap.set( category.slug, {
                        item: category,
                        count: 0
                    } );
                }

                categoryCountMap.get( category.slug )!.count++;
            } );
        } );

        const categoriesByObservationCount: Countable< Category >[] = [ ...categoryCountMap.values() ];
        const categoriesByAscendingObservationCount: Countable< Category >[] =
            categoriesByObservationCount.sort( ( container1: Countable< Category >, container2: Countable< Category > ) => container1.count - container2.count );

        return categoriesByAscendingObservationCount.map( ( container: Countable< Category > ) => container.item );
    } );

    return {
        // ACTIONS
        fetchWebinarBySlugIfMissing,
        initialize,
        nUpcomingWebinars,
        reset,
        webinarById,
        webinarBySlug,

        // GETTERS
        allWebinars,
        initialized,
        isLoading,
        webinarCategories
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useWebinarsStore, import.meta.hot ) );
}
